import { alpha, darken } from '@material-ui/core/styles';

import { getSettings } from 'utils/settings';

/**
 * White label — tokens de MARCA resueltos en runtime (guía §1 y §5).
 *
 * Regla: ningún color de marca puede quedar embebido en el bundle. Todo color
 * que dependa del tenant se deriva aquí de `theme.colors` del JSON del tenant
 * (`/static/json/{hostname}.json`), cargado por `initSettings()` en el
 * bootstrap (App espera a que resuelva antes de renderizar, por lo que
 * cualquier consumo de `getBrand()` ocurre con settings ya disponibles).
 *
 * Fallbacks: los neutros de producción. Ante un error de configuración,
 * ningún tenant debe ver la marca de otro cliente.
 */
export const BRAND_FALLBACK = {
  primary: '#012B55',
  secondary: '#002B55',
};

function safeDarken(color, coefficient) {
  try {
    return darken(color, coefficient);
  } catch (error) {
    return color;
  }
}

function safeAlpha(color, opacity) {
  try {
    return alpha(color, opacity);
  } catch (error) {
    return color;
  }
}

/**
 * Deriva los tokens de marca a partir de `theme.colors` del tenant.
 * `primaryHover` y `primarySoft` se calculan del primary para no exigir
 * claves nuevas a los JSONs existentes (requisito §9: soportar los 12
 * JSONs sin modificarlos).
 *
 * @param {{primary?: string, secondary?: string, sidebar?: string}} [colors]
 */
export function resolveBrand(colors) {
  const primary = colors?.primary || BRAND_FALLBACK.primary;
  const secondary = colors?.secondary || BRAND_FALLBACK.secondary;
  const sidebar = colors?.sidebar || primary;

  return {
    primary,
    primaryHover: safeDarken(primary, 0.18),
    primarySoft: safeAlpha(primary, 0.12),
    secondary,
    sidebar,
  };
}

let cachedBrand = null;

/**
 * Acceso síncrono a los tokens de marca desde cualquier punto (estilos,
 * renders, funciones puras). Memoizado: la config del tenant no cambia
 * dentro de una misma carga de página (initSettings corre una vez en el
 * bootstrap), y así se evita descifrar localStorage en cada render.
 */
export function getBrand() {
  if (!cachedBrand) {
    cachedBrand = resolveBrand(getSettings('theme.colors'));
  }

  return cachedBrand;
}
